/**
 * ScreenWorkspacePage — /screen route for Workspace Shell V1
 * 
 * Purpose: Turn digest review into a fast screening queue.
 * UX Rule: Screen is for fast triage, not deep analysis.
 * 
 * Layout (Updated):
 * - DigestChipsBar (top): Compact digest profile cards for quick navigation
 * - Main workspace: screening queue
 * - ScreenPreviewDrawer (right): selected article preview
 * 
 * Features:
 * - Primary digest shown first (marked with star)
 * - "Screen All" mode to see unscreened from all digests
 * - Articles grouped by digest when in "Screen All" mode
 * 
 * Keyboard shortcuts:
 * - J/K: Navigate to next/previous article
 * - S: Save to Library
 * - X: Skip
 * - O: Open preview/PubMed
 */
import React from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useWorkspace } from '../../components/workspace/WorkspaceShell';
import { SharedDigestFilterBar } from '../../components/shared/SharedDigestFilterBar';
import { DigestProfileStat } from '../../components/workspace/screen/DigestChipsBar';
import {
  getPersistedDigestProfileId,
  setPersistedDigestProfileId,
} from '../../utils/sharedDigestSelection';
import { ScreenQueueCard } from '../../components/workspace/screen/ScreenQueueCard';
import { ScreenPreviewDrawer } from '../../components/workspace/screen/ScreenPreviewDrawer';
import { 
  workspaceAPI, 
  DigestSession, 
  ScreenProgress, 
  ScreenQueueArticle, 
  ScreenQueueResponse,
  preferencesAPI,
} from '../../api';
import { API_BASE_URL } from 'apiConfig';
import { toast } from 'sonner';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { getSavedViews, createSavedView, renameSavedView, deleteSavedView, SavedView } from '../../config/savedViews';
import { 
  trackPageView, 
  trackScreeningAction, 
  trackScreeningComplete 
} from '../../utils/analytics';
import {
  PartyPopper,
  Search,
  GraduationCap,
  Clock,
  Inbox,
  Filter,
  ChevronDown,
  Play,
  BookOpen,
  Loader2,
  X,
  MoveHorizontal,
  Bookmark,
  Save,
  Trash2,
  Pencil,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useAuth } from '../../contexts/AuthContext';

type FilterStatus = 'unscreened' | 'deferred' | 'saved' | 'all';

const VALID_FILTER_STATUSES: FilterStatus[] = ['unscreened', 'deferred', 'saved', 'all'];

function matchesFilterStatus(article: ScreenQueueArticle, status: FilterStatus): boolean {
  if (status === 'all') return true;
  const screeningStatus = article.screening_status ?? null;
  switch (status) {
    case 'unscreened':
      return screeningStatus === null;
    case 'saved':
      return screeningStatus === 'keep' || article.is_in_library === true;
    case 'deferred':
      return screeningStatus === 'later';
    default:
      return true;
  }
}

function filterArticlesByStatus(
  items: ScreenQueueArticle[],
  status: FilterStatus
): ScreenQueueArticle[] {
  return items.filter((article) => matchesFilterStatus(article, status));
}

// Get user tier from user object
const getUserTier = (user: any): 'pro' | 'verified' | 'free' => {
  const isPremium = user?.plan_tier === 'premium';
  const hasActiveTrial = user?.trial_ends_at && new Date(user.trial_ends_at) > new Date();
  
  if (isPremium || hasActiveTrial) return 'pro';
  if (user?.peer_verification_status === 'verified') return 'verified';
  return 'free';
};

// Group articles by profile_id for "Screen All" mode
interface ArticleGroup {
  profile_id: string;
  profile_label: string;
  articles: ScreenQueueArticle[];
}

const ScreenWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshBootstrap } = useWorkspace();
  const { user } = useAuth();
  const navV2Enabled = useFeatureFlag('enable_navigation_v2');
  const routeParams = useParams<{ profileId?: string; pmid?: string }>();
  const location = useLocation();
  const isQueueRoute = navV2Enabled && location.pathname.includes('/screen/queues/');
  const isArticleRoute = navV2Enabled && location.pathname.includes('/screen/articles/');
  
  // Get user tier for digest limits
  const userTier = getUserTier(user);

  // State
  const [profileStats, setProfileStats] = React.useState<DigestProfileStat[]>([]);
  const [totalUnscreened, setTotalUnscreened] = React.useState(0);
  // Initialize selectedProfileId: path param (Nav V2) > query param > persisted > null
  const initialProfileId =
    (navV2Enabled && routeParams.profileId) ||
    searchParams.get('profileId') ||
    getPersistedDigestProfileId() ||
    null;
  const [selectedProfileId, setSelectedProfileId] = React.useState<string | null>(initialProfileId);
  const initialStatusParam = searchParams.get('status') as FilterStatus | null;
  const [filterStatus, setFilterStatus] = React.useState<FilterStatus>(
    initialStatusParam && VALID_FILTER_STATUSES.includes(initialStatusParam)
      ? initialStatusParam
      : 'unscreened'
  );
  const [queueArticles, setQueueArticles] = React.useState<ScreenQueueArticle[]>([]);
  const [queueArticleGroups, setQueueArticleGroups] = React.useState<ArticleGroup[]>([]);
  const articles = React.useMemo(
    () => filterArticlesByStatus(queueArticles, filterStatus),
    [queueArticles, filterStatus]
  );
  const articleGroups = React.useMemo(
    () =>
      queueArticleGroups
        .map((group) => ({
          ...group,
          articles: filterArticlesByStatus(group.articles, filterStatus),
        }))
        .filter((group) => group.articles.length > 0),
    [queueArticleGroups, filterStatus]
  );
  const [progress, setProgress] = React.useState<ScreenProgress>({
    total: 0,
    saved: 0,
    deferred: 0,
    skipped: 0,
    remaining: 0,
  });
  const [loading, setLoading] = React.useState(true);
  const [statsRefreshing, setStatsRefreshing] = React.useState(false);
  const [queueLoading, setQueueLoading] = React.useState(false);
  const [processingPmid, setProcessingPmid] = React.useState<string | null>(null);

  // Preview drawer state
  const [selectedArticle, setSelectedArticle] = React.useState<ScreenQueueArticle | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  // Current selection index for keyboard navigation
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // ── Nav V2: Route-param sync (flag-gated) ──
  React.useEffect(() => {
    if (!navV2Enabled) return;
    if (isQueueRoute && routeParams.profileId) {
      setSelectedProfileId(routeParams.profileId);
      setPersistedDigestProfileId(routeParams.profileId);
    } else if (!isQueueRoute && !isArticleRoute) {
      const persisted = getPersistedDigestProfileId();
      if (persisted) {
        setSelectedProfileId(persisted);
        navigate(`/screen/queues/${persisted}`, { replace: true });
      }
    }
  }, [navV2Enabled, isQueueRoute, isArticleRoute, routeParams.profileId, location.pathname]);

  // Nav V2: Article focus — find article in current queue context
  const [focusedPmid, setFocusedPmid] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!navV2Enabled || !isArticleRoute || !routeParams.pmid) {
      setFocusedPmid(null);
      return;
    }
    // If profileId context provided in query params, set it
    const qpProfileId = searchParams.get('profileId');
    if (qpProfileId) setSelectedProfileId(qpProfileId);
    setFocusedPmid(routeParams.pmid);
  }, [navV2Enabled, isArticleRoute, routeParams.pmid, location.pathname]);

  // Nav V2: Scroll to and focus article after queue loads
  React.useEffect(() => {
    if (!focusedPmid || queueLoading || loading) return;
    const matched = articles.find(a => a.pmid === focusedPmid);
    if (matched) {
      setSelectedArticle(matched);
      setPreviewOpen(true);
      const timeout = setTimeout(() => {
        const el = document.querySelector(`[data-testid="screen-card-${focusedPmid}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-sky-400', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-sky-400', 'ring-offset-2'), 3000);
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [focusedPmid, queueLoading, loading, articles]);

  // Nav V2: Route-aware chip handler
  const handleSelectProfile = React.useCallback((profileId: string | null) => {
    setPersistedDigestProfileId(profileId);
    if (navV2Enabled) {
      navigate(profileId ? `/screen/queues/${profileId}` : '/screen');
    } else {
      setSelectedProfileId(profileId);
    }
  }, [navV2Enabled, navigate]);

  // Track which PMIDs we've already requested regeneration for (prevent loops)
  const regenerationRequestedRef = React.useRef<Set<string>>(new Set());
  // Track if a regeneration request is in flight
  const regenerationInFlightRef = React.useRef(false);

  // State for running digest
  const [runningDigestProfile, setRunningDigestProfile] = React.useState<string | null>(null);

  // Saved views — flag-gated, localStorage-backed
  const savedViewsEnabled = useFeatureFlag('enable_saved_views');
  const svUserId = user?.user_id || 'anon';
  const [savedViews, setSavedViews] = React.useState<SavedView[]>([]);
  const [activeViewId, setActiveViewId] = React.useState<string | null>(null);
  const [showSaveInput, setShowSaveInput] = React.useState(false);
  const [newViewName, setNewViewName] = React.useState('');
  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState('');

  React.useEffect(() => {
    if (savedViewsEnabled) setSavedViews(getSavedViews(svUserId, 'litscreen'));
  }, [savedViewsEnabled, svUserId]);

  const handleSaveView = () => {
    if (!newViewName.trim()) return;
    const view = createSavedView(svUserId, 'litscreen', newViewName.trim(), {
      profileId: selectedProfileId,
      filterStatus: filterStatus,
    });
    setSavedViews(getSavedViews(svUserId, 'litscreen'));
    setActiveViewId(view.id);
    setNewViewName('');
    setShowSaveInput(false);
    toast.success(`View "${view.name}" saved`);
  };

  const handleSelectView = (view: SavedView) => {
    if (view.filters.profileId !== undefined) handleSelectProfile(view.filters.profileId || null);
    if (view.filters.filterStatus) setFilterStatus(view.filters.filterStatus as 'unscreened' | 'deferred' | 'saved' | 'all');
    setActiveViewId(view.id);
  };

  const handleClearView = () => {
    setActiveViewId(null);
  };

  const handleDeleteView = (viewId: string) => {
    deleteSavedView(svUserId, 'litscreen', viewId);
    setSavedViews(getSavedViews(svUserId, 'litscreen'));
    if (activeViewId === viewId) setActiveViewId(null);
    toast.success('View deleted');
  };

  const handleRenameView = (viewId: string) => {
    if (!renameValue.trim()) return;
    renameSavedView(svUserId, 'litscreen', viewId, renameValue.trim());
    setSavedViews(getSavedViews(svUserId, 'litscreen'));
    setRenamingId(null);
    setRenameValue('');
  };

  // Load profile stats on mount
  React.useEffect(() => {
    trackPageView('screen');
    loadProfileStats();
  }, []);

  // Load queue when profile or profileStats changes (filter is applied client-side)
  React.useEffect(() => {
    loadQueue();
  }, [selectedProfileId, profileStats]);

  // Update URL params
  const updateUrlParams = (profileId: string | null, status: FilterStatus) => {
    setPersistedDigestProfileId(profileId);
    const params = new URLSearchParams();
    if (profileId) params.set('profileId', profileId);
    if (status !== 'unscreened') params.set('status', status);
    setSearchParams(params, { replace: true });
  };

  // Load profile stats for chips bar
  const loadProfileStats = async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    try {
      if (!silent) {
        if (profileStats.length === 0) {
          setLoading(true);
        } else {
          setStatsRefreshing(true);
        }
      }
      const response = await preferencesAPI.getProfilesScreenStats();
      setProfileStats(response.profiles || []);
      setTotalUnscreened(response.total_unscreened || 0);
      
      if (!navV2Enabled && !selectedProfileId && response.profiles?.length > 0) {
        const primary = response.profiles.find(p => p.is_primary);
        if (primary) {
          setSelectedProfileId(primary.profile_id);
        } else {
          setSelectedProfileId(response.profiles[0].profile_id);
        }
      }
    } catch (error) {
      console.error('Failed to load profile stats:', error);
      if (!silent) {
        toast.error('Failed to load queue profiles');
      }
    } finally {
      setLoading(false);
      setStatsRefreshing(false);
    }
  };

  const applyProfileStatsResponse = (statsResp: {
    profiles?: DigestProfileStat[];
    total_unscreened?: number;
  }) => {
    setProfileStats(statsResp.profiles || []);
    setTotalUnscreened(statsResp.total_unscreened || 0);
  };

  // Stale-request protection: increment on every loadQueue call, check before writing state
  const loadQueueTokenRef = React.useRef(0);

  // Load queue
  const loadQueue = async () => {
    // Defer until profileStats has loaded — the profileStats dependency in
    // the useEffect guarantees a re-fire once stats arrive, preventing
    // an empty-state flash during component remount (e.g. /screen → /screen/queues/:id).
    if (profileStats.length === 0) return;
    
    const requestToken = ++loadQueueTokenRef.current;
    
    try {
      setQueueLoading(true);
      
      if (selectedProfileId === null) {
        // Aggregated "Screen All" mode — load from all profiles using last_digest_id
        const allArticles: ScreenQueueArticle[] = [];
        const groups: ArticleGroup[] = [];
        let totalProgress: ScreenProgress = { total: 0, saved: 0, deferred: 0, skipped: 0, remaining: 0 };
        
        for (const profile of profileStats) {
          if (!profile.last_digest_id) continue; // Skip profiles without a digest
          
          const response: ScreenQueueResponse = await workspaceAPI.getScreenQueue({
            digest_id: profile.last_digest_id,
            status: 'all',
            limit: 50,
          });
          
          // Stale check: if user switched view during fetch, discard
          if (loadQueueTokenRef.current !== requestToken) return;
          
          if (response.articles.length > 0) {
            const articlesWithProfile = response.articles.map(a => ({
              ...a,
              _profile_id: profile.profile_id,
              _profile_label: profile.subspecialty_label 
                ? `${profile.specialty_label} - ${profile.subspecialty_label}`
                : profile.specialty_label,
            }));
            
            allArticles.push(...articlesWithProfile);
            groups.push({
              profile_id: profile.profile_id,
              profile_label: profile.subspecialty_label 
                ? `${profile.specialty_label} - ${profile.subspecialty_label}`
                : profile.specialty_label,
              articles: articlesWithProfile,
            });
          }
          
          totalProgress.total += response.progress.total;
          totalProgress.saved += response.progress.saved;
          totalProgress.deferred += response.progress.deferred;
          totalProgress.skipped += response.progress.skipped;
          totalProgress.remaining += response.progress.remaining;
        }
        
        // Final stale check before writing state
        if (loadQueueTokenRef.current !== requestToken) return;
        
        setQueueArticles(allArticles);
        setQueueArticleGroups(groups);
        setProgress(totalProgress);
      } else {
        // Single profile mode
        const profile = profileStats.find(p => p.profile_id === selectedProfileId);
        if (!profile?.last_digest_id) {
          setQueueArticles([]);
          setQueueArticleGroups([]);
          setProgress({ total: 0, saved: 0, deferred: 0, skipped: 0, remaining: 0 });
          return;
        }
        
        const response: ScreenQueueResponse = await workspaceAPI.getScreenQueue({
          digest_id: profile.last_digest_id,
          status: 'all',
          limit: 100,
        });
        
        // Stale check
        if (loadQueueTokenRef.current !== requestToken) return;
        
        setQueueArticles(response.articles);
        setQueueArticleGroups([]);
        setProgress(response.progress);
      }
      
      setSelectedIndex(0);
    } catch (error) {
      console.error('Failed to load queue:', error);
      toast.error('Failed to load screening queue');
    } finally {
      // Only clear loading for the current (non-stale) request.
      // Stale requests must not flip queueLoading, as this would trigger
      // a premature queueHasLoaded transition with articles still empty,
      // causing the auto-switch to redirect to /screen.
      if (loadQueueTokenRef.current === requestToken) {
        setQueueLoading(false);
      }
    }
  };

  // Handle profile selection
  // Handle filter change
  const handleFilterChange = (status: FilterStatus) => {
    setFilterStatus(status);
    setSelectedIndex(0);
    updateUrlParams(selectedProfileId, status);
  };

  // Handle set primary
  const handleSetPrimary = async (profileId: string) => {
    try {
      await preferencesAPI.setPrimaryProfile(profileId);
      toast.success('Primary queue updated');
      await loadProfileStats();
    } catch (error) {
      toast.error('Failed to set primary queue');
    }
  };

  // Handle screening decision
  const handleDecision = async (article: ScreenQueueArticle, decision: 'keep' | 'later' | 'skip') => {
    if (processingPmid) return;
    
    try {
      setProcessingPmid(article.pmid);
      
      await workspaceAPI.recordScreeningDecision({
        article_id: article.pmid,
        decision,
        digest_id: article.digest_id,
      });

      // Track the action - map decision to action type
      const actionMap: Record<string, 'saved' | 'deferred' | 'skipped'> = {
        keep: 'saved',
        later: 'deferred',
        skip: 'skipped',
      };
      trackScreeningAction(actionMap[decision], article.pmid, article.digest_id);

      // Update local queue — keep article in full queue with new status so filters work
      const decisionStatus: ScreenQueueArticle['screening_status'] =
        decision === 'keep' ? 'keep' : decision === 'later' ? 'later' : 'skip';

      const updateArticleStatus = (a: ScreenQueueArticle): ScreenQueueArticle =>
        a.pmid === article.pmid
          ? {
              ...a,
              screening_status: decisionStatus,
              is_in_library: decision === 'keep' ? true : a.is_in_library,
            }
          : a;

      const newArticles = queueArticles.map(updateArticleStatus);
      setQueueArticles(newArticles);
      
      // Update article groups if in "Screen All" mode
      if (selectedProfileId === null) {
        setQueueArticleGroups(prev => prev.map(group => ({
          ...group,
          articles: group.articles.map(updateArticleStatus),
        })));
      }

      // Update progress
      setProgress(prev => {
        const newProgress = { ...prev };
        newProgress.remaining = Math.max(0, prev.remaining - 1);
        if (decision === 'keep') newProgress.saved++;
        else if (decision === 'later') newProgress.deferred++;
        else newProgress.skipped++;
        return newProgress;
      });

      // Close preview if open
      if (previewOpen && selectedArticle?.pmid === article.pmid) {
        setPreviewOpen(false);
      }

      // Move selection to next visible article in current filter
      const visibleArticles = filterArticlesByStatus(newArticles, filterStatus);
      if (selectedIndex >= visibleArticles.length && visibleArticles.length > 0) {
        setSelectedIndex(visibleArticles.length - 1);
      }

      // Check for completion
      if (
        filterArticlesByStatus(newArticles, 'unscreened').length === 0 &&
        filterStatus === 'unscreened'
      ) {
        // Track completion with digest ID and stats
        const digestId = article.digest_id || 'unknown';
        trackScreeningComplete(digestId, {
          saved: progress.saved,
          deferred: progress.deferred,
          skipped: progress.skipped,
          total: progress.total,
        });
        refreshBootstrap();
        loadProfileStats({ silent: true }); // Refresh stats
      }
    } catch (error) {
      console.error('Screening error:', error);
      toast.error('Failed to process article');
    } finally {
      setProcessingPmid(null);
    }
  };

  // Keyboard navigation
  useHotkeys('j', () => {
    if (selectedIndex < articles.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  }, { enableOnFormTags: false });

  useHotkeys('k', () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  }, { enableOnFormTags: false });

  useHotkeys('s', () => {
    const article = articles[selectedIndex];
    if (article && !processingPmid) {
      handleDecision(article, 'keep');
    }
  }, { enableOnFormTags: false });

  useHotkeys('x', () => {
    const article = articles[selectedIndex];
    if (article && !processingPmid) {
      handleDecision(article, 'skip');
    }
  }, { enableOnFormTags: false });

  useHotkeys('o', () => {
    const article = articles[selectedIndex];
    if (article) {
      window.open(`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`, '_blank');
    }
  }, { enableOnFormTags: false });

  useHotkeys('enter', () => {
    const article = articles[selectedIndex];
    if (article) {
      setSelectedArticle(article);
      setPreviewOpen(true);
    }
  }, { enableOnFormTags: false });

  // Run digest now handler
  // Run Queue Now — polling refs for background generation tracking
  const activePolls = React.useRef<Map<string, { intervalId: NodeJS.Timeout; snapshotDigestId: string | null; snapshotCount: number }>>(new Map());
  const selectedProfileIdRef = React.useRef(selectedProfileId);
  React.useEffect(() => { selectedProfileIdRef.current = selectedProfileId; }, [selectedProfileId]);

  // Cleanup all polls on unmount
  React.useEffect(() => {
    return () => {
      activePolls.current.forEach(poll => clearInterval(poll.intervalId));
      activePolls.current.clear();
    };
  }, []);

  // Run Queue Now — poll for completion (every 20s, max ~3 min)
  const RUN_DIGEST_POLL_MS = 20000;
  const RUN_DIGEST_MAX_POLLS = 9;

  const handleRunDigestNow = async (profileId: string) => {
    if (activePolls.current.has(profileId)) return;

    setRunningDigestProfile(profileId);
    try {
      const response = await fetch(`${API_BASE_URL}/preferences/profiles/${profileId}/run-digest`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to run queue');

      const currentProfile = profileStats.find(p => p.profile_id === profileId);
      const snapshotDigestId = currentProfile?.last_digest_id || null;
      const snapshotCount = currentProfile?.unscreened_count || 0;

      toast.success('Generating queue... New articles will appear automatically.');

      let pollCount = 0;

      const intervalId = setInterval(async () => {
        if (document.hidden) return;

        pollCount++;

        try {
          const statsResp = await preferencesAPI.getProfilesScreenStats();
          const updatedProfile = (statsResp.profiles || []).find((p) => p.profile_id === profileId);

          if (updatedProfile) {
            const digestChanged =
              updatedProfile.last_digest_id && updatedProfile.last_digest_id !== snapshotDigestId;
            const countChanged = updatedProfile.unscreened_count !== snapshotCount;

            if (digestChanged || countChanged) {
              clearInterval(intervalId);
              activePolls.current.delete(profileId);
              setRunningDigestProfile(prev => (prev === profileId ? null : prev));
              applyProfileStatsResponse(statsResp);

              const currentViewedId = selectedProfileIdRef.current;
              if (currentViewedId === profileId || currentViewedId === null) {
                loadQueue();
              }

              toast.success('New articles ready!');
              return;
            }
          }
        } catch {
          /* network error — continue polling */
        }

        if (pollCount >= RUN_DIGEST_MAX_POLLS) {
          clearInterval(intervalId);
          activePolls.current.delete(profileId);
          setRunningDigestProfile(prev => (prev === profileId ? null : prev));
          toast('Still generating. New articles will appear on your next visit.', { duration: 5000 });
        }
      }, RUN_DIGEST_POLL_MS);

      activePolls.current.set(profileId, { intervalId, snapshotDigestId, snapshotCount });

    } catch (error) {
      console.error('Failed to run digest:', error);
      toast.error('Failed to run queue');
      setRunningDigestProfile(null);
    }
  };

  // Suppress digest handler
  const handleSuppressDigest = async (profileId: string, duration: { value: number; unit: string }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/preferences/profiles/${profileId}/suppress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(duration),
      });
      
      if (!response.ok) {
        throw new Error('Failed to suppress queue');
      }
      
      loadProfileStats({ silent: true });
    } catch (error) {
      console.error('Failed to suppress digest:', error);
      throw error;
    }
  };

  // Delete digest handler
  const handleDeleteDigest = async (profileId: string) => {
    try {
      await preferencesAPI.deleteProfile(profileId);
      loadProfileStats({ silent: true });
      
      // If we deleted the selected profile, switch to another one
      if (selectedProfileId === profileId) {
        const remaining = profileStats.filter(p => p.profile_id !== profileId);
        if (remaining.length > 0) {
          const primary = remaining.find(p => p.is_primary) || remaining[0];
          setSelectedProfileId(primary.profile_id);
        } else {
          setSelectedProfileId(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete digest:', error);
      throw error;
    }
  };

  // Check if caught up — no unscreened articles remain (only after queue actually loaded)
  const [queueHasLoaded, setQueueHasLoaded] = React.useState(false);
  const prevQueueLoading = React.useRef(false);
  React.useEffect(() => {
    // Mark queue as loaded once it transitions from loading→done
    if (prevQueueLoading.current && !queueLoading) {
      setQueueHasLoaded(true);
    }
    prevQueueLoading.current = queueLoading;
  }, [queueLoading]);

  const isCaughtUp =
    queueHasLoaded &&
    filterStatus === 'unscreened' &&
    articles.length === 0 &&
    progress.remaining === 0 &&
    !queueLoading &&
    !loading;

  // Get the current profile label for header
  const currentProfileLabel = selectedProfileId 
    ? profileStats.find(p => p.profile_id === selectedProfileId)?.specialty_label || 'Digest'
    : 'All Queues';
    
  // Get primary profile for showing recent digests
  const primaryProfile = profileStats.find(p => p.is_primary) || profileStats[0];

  return (
    <div className="flex flex-col" data-testid="screen-workspace-page">
      {/* Digest Filter Bar — centered with Run Digest buttons */}
      {!loading && profileStats.length > 0 && (
        <SharedDigestFilterBar
          profiles={profileStats}
          totalUnscreened={totalUnscreened}
          statsLoading={statsRefreshing}
          onRefreshStats={() => loadProfileStats({ silent: true })}
          selectedProfileId={selectedProfileId}
          onSelectProfile={(id) => handleSelectProfile(id)}
          showUnscreenedBadge={true}
          showSavedPercent={true}
          showPrimaryStar={true}
          showRunDigest={true}
          onRunDigest={handleRunDigestNow}
          runningProfileId={runningDigestProfile}
          centered={true}
          allLabel="All Digests"
        />
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Queue Header */}
        <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))] flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
            <h2 className="text-sm font-medium text-[hsl(var(--foreground))] leading-snug">
              {selectedProfileId === null ? 'All Queues' : currentProfileLabel}
              {' - '}
              {filterStatus === 'unscreened' ? 'Screening Queue' : 
               filterStatus === 'deferred' ? 'Deferred Items' :
               filterStatus === 'saved' ? 'Saved Items' : 'All Items'}
            </h2>
            {!queueLoading && (
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                {articles.length} {articles.length === 1 ? 'article' : 'articles'}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Screening progress counter */}
            {!queueLoading && progress.total > 0 && (
              <div className="flex items-center gap-2" data-testid="screening-progress">
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">{progress.total - progress.remaining}</span>
                  <span>of</span>
                  <span className="font-semibold text-slate-700">{progress.total}</span>
                  <span>screened</span>
                </div>
                <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-sky-500 rounded-full transition-all duration-500"
                    style={{ width: `${progress.total > 0 ? ((progress.total - progress.remaining) / progress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Filter Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                {filterStatus === 'unscreened' ? 'Unscreened' :
                 filterStatus === 'deferred' ? 'Deferred' :
                 filterStatus === 'saved' ? 'Saved' : 'All'}
                <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleFilterChange('unscreened')}>
                Unscreened {progress.remaining > 0 && <Badge className="ml-2 h-5">{progress.remaining}</Badge>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterChange('deferred')}>
                Deferred {progress.deferred > 0 && <Badge variant="secondary" className="ml-2 h-5">{progress.deferred}</Badge>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterChange('saved')}>
                Saved {progress.saved > 0 && <Badge variant="secondary" className="ml-2 h-5">{progress.saved}</Badge>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterChange('all')}>
                All
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Saved Views — flag-gated */}
        {savedViewsEnabled && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 flex-wrap" data-testid="screen-saved-views-bar">
            <Bookmark className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            {savedViews.map(v => (
              <div key={v.id} className="group flex items-center gap-0.5">
                {renamingId === v.id ? (
                  <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleRenameView(v.id); if (e.key === 'Escape') setRenamingId(null); }}
                    onBlur={() => handleRenameView(v.id)} className="px-2 py-0.5 text-xs border rounded w-24" />
                ) : (
                  <button onClick={() => handleSelectView(v)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      activeViewId === v.id ? 'bg-sky-100 border-sky-300 text-sky-700 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:border-sky-300'
                    }`} data-testid={`screen-saved-view-${v.id}`}>{v.name}</button>
                )}
                <button onClick={() => { setRenamingId(v.id); setRenameValue(v.name); }}
                  className="p-0.5 text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Rename">
                  <Pencil className="w-3 h-3" /></button>
                <button onClick={() => handleDeleteView(v.id)}
                  className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete">
                  <Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            {activeViewId && (<button onClick={handleClearView} className="px-2 py-1 text-xs text-slate-400 hover:text-slate-600">Clear</button>)}
            {showSaveInput ? (
              <div className="flex items-center gap-1">
                <input autoFocus value={newViewName} onChange={e => setNewViewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveView(); if (e.key === 'Escape') setShowSaveInput(false); }}
                  placeholder="View name..." className="px-2 py-0.5 text-xs border rounded w-28" data-testid="screen-new-view-input" />
                <button onClick={handleSaveView} className="p-1 text-sky-600"><Save className="w-3.5 h-3.5" /></button>
                <button onClick={() => setShowSaveInput(false)} className="p-1 text-slate-400"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <button onClick={() => setShowSaveInput(true)} className="px-2 py-1 text-xs text-sky-600 hover:text-sky-700 font-medium" data-testid="screen-save-view-btn">
                + Save View
              </button>
            )}
          </div>
        )}

        {/* Queue Content */}
        <div className="flex-1">
          <div className="p-4">
            <div className="max-w-3xl mx-auto space-y-3">
              {/* Mobile swipe tip — only shows on touch devices, dismissable */}
              {!queueLoading && !loading && articles.length > 0 && !localStorage.getItem('litscreen_swipe_tip_dismissed') && (
                <div className="flex items-center gap-2 px-3 py-2 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-700 md:hidden" data-testid="swipe-tip">
                  <MoveHorizontal className="w-4 h-4 shrink-0" />
                  <span className="flex-1">Swipe right to <b>Save</b>, swipe left to <b>Skip</b></span>
                  <button 
                    onClick={() => { localStorage.setItem('litscreen_swipe_tip_dismissed', 'true'); }}
                    className="p-0.5 text-sky-400 hover:text-sky-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              {queueLoading || loading ? (
                // Loading skeleton
                <div className="space-y-3" data-testid="screen-loading">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="p-4 border border-[hsl(var(--border))] rounded-lg">
                      <Skeleton className="h-5 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/3 mb-3" />
                      <Skeleton className="h-12 w-full mb-3" />
                      <div className="flex gap-2">
                        <Skeleton className="h-8 w-20" />
                        <Skeleton className="h-8 w-20" />
                        <Skeleton className="h-8 w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : isArticleRoute && focusedPmid && !queueLoading && !articles.some(a => a.pmid === focusedPmid) ? (
                <div className="text-center py-16" data-testid="screen-article-not-found">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-800 mb-2">This article isn't in this queue.</h3>
                  <p className="text-sm text-slate-500 mb-4">PMID {focusedPmid}</p>
                  <button onClick={() => navigate(selectedProfileId ? `/screen/queues/${selectedProfileId}` : '/screen')}
                    className="text-sm text-sky-600 hover:text-sky-700 font-medium">
                    ← Back to queue
                  </button>
                </div>
              ) : isCaughtUp ? (
                // Show recent digests with action buttons instead of empty "caught up" message
                <div className="space-y-6" data-testid="recent-digests-view">
                  {/* Header with action buttons */}
                  <div className="text-center py-6">
                    <PartyPopper className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2">
                      You're all caught up!
                    </h3>
                    <p className="text-[hsl(var(--muted-foreground))] mb-4">
                      No new articles to screen. Here are your most recent queues.
                    </p>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      <Button
                        onClick={() => primaryProfile && handleRunDigestNow(primaryProfile.profile_id)}
                        disabled={runningDigestProfile !== null}
                        className="bg-sky-600 hover:bg-sky-700 text-white"
                        data-testid="run-digest-now-btn"
                      >
                        {runningDigestProfile ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Run Queue Now
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/library/shelf')}
                        data-testid="review-in-lithub-btn"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Review in LitHub
                      </Button>
                      {progress.deferred > 0 && (
                        <Button 
                          variant="outline" 
                          onClick={() => handleFilterChange('deferred')}
                          className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Review deferred ({progress.deferred})
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Recent digests label and summary */}
                  <div className="border-t pt-6">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
                      Your Most Recent Digests
                    </h4>
                    
                    {/* Summary cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="p-4 bg-sky-50 rounded-xl border border-sky-100 text-center">
                        <div className="text-2xl font-bold text-sky-700">{progress.saved}</div>
                        <div className="text-xs text-sky-600 uppercase tracking-wide">Saved to LitHub</div>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 text-center">
                        <div className="text-2xl font-bold text-amber-700">{progress.deferred}</div>
                        <div className="text-xs text-amber-600 uppercase tracking-wide">Deferred</div>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center">
                        <div className="text-2xl font-bold text-slate-700">{progress.skipped}</div>
                        <div className="text-xs text-slate-600 uppercase tracking-wide">Skipped</div>
                      </div>
                    </div>
                    
                    {/* Quick action to view saved */}
                    {progress.saved > 0 && (
                      <div className="mt-4 text-center">
                        <Button
                          variant="link"
                          onClick={() => navigate('/library/shelf')}
                          className="text-sky-600"
                        >
                          View {progress.saved} saved article{progress.saved !== 1 ? 's' : ''} in LitHub →
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : articles.length === 0 ? (
                // Empty state
                <div className="text-center py-16" data-testid="screen-empty">
                  <Inbox className="w-16 h-16 text-[hsl(var(--muted-foreground))] mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2">
                    No articles to show
                  </h3>
                  <p className="text-[hsl(var(--muted-foreground))] mb-6">
                    {filterStatus === 'unscreened' 
                      ? 'No unscreened articles.'
                      : filterStatus === 'deferred'
                      ? 'No deferred articles.'
                      : filterStatus === 'saved'
                      ? 'No saved articles.'
                      : 'No articles found.'}
                  </p>
                  {filterStatus !== 'unscreened' && (
                    <Button onClick={() => handleFilterChange('unscreened')}>
                      View unscreened
                    </Button>
                  )}
                </div>
              ) : selectedProfileId === null && articleGroups.length > 0 ? (
                // "Screen All" mode - grouped by digest
                <div className="space-y-6" data-testid="screen-queue-grouped">
                  {articleGroups.map((group) => (
                    <div key={group.profile_id}>
                      {/* Section Header */}
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                        <Badge variant="outline" className="text-xs font-medium">
                          {group.profile_label}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {group.articles.length} {group.articles.length === 1 ? 'article' : 'articles'}
                        </span>
                      </div>
                      
                      {/* Articles in this group */}
                      <div className="space-y-3">
                        {group.articles.map((article, idx) => {
                          const globalIndex = articles.findIndex(a => a.pmid === article.pmid);
                          return (
                            <ScreenQueueCard
                              key={article.pmid}
                              article={article}
                              index={globalIndex}
                              isSelected={selectedIndex === globalIndex}
                              isProcessing={processingPmid === article.pmid}
                              onSelect={() => setSelectedIndex(globalIndex)}
                              onSave={() => handleDecision(article, 'keep')}
                              onDefer={() => handleDecision(article, 'later')}
                              onSkip={() => handleDecision(article, 'skip')}
                              onRunDigestNow={handleRunDigestNow}
                              onSuppressDigest={handleSuppressDigest}
                              onDeleteDigest={handleDeleteDigest}
                              profileId={group.profile_id}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Single digest mode - flat list
                <div className="space-y-4" data-testid="screen-queue">
                  {articles.map((article, index) => (
                    <ScreenQueueCard
                      key={article.pmid}
                      article={article}
                      index={index}
                      isSelected={selectedIndex === index}
                      isProcessing={processingPmid === article.pmid}
                      onSelect={() => setSelectedIndex(index)}
                      onSave={() => handleDecision(article, 'keep')}
                      onDefer={() => handleDecision(article, 'later')}
                      onSkip={() => handleDecision(article, 'skip')}
                      onRunDigestNow={handleRunDigestNow}
                      onSuppressDigest={handleSuppressDigest}
                      onDeleteDigest={handleDeleteDigest}
                      profileId={selectedProfileId || undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Drawer */}
      <ScreenPreviewDrawer
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        article={selectedArticle}
        isProcessing={processingPmid === selectedArticle?.pmid}
        onSave={() => selectedArticle && handleDecision(selectedArticle, 'keep')}
        onDefer={() => selectedArticle && handleDecision(selectedArticle, 'later')}
        onSkip={() => selectedArticle && handleDecision(selectedArticle, 'skip')}
        onOpenInLearn={() => navigate('/library/shelf')}
      />
    </div>
  );
};

export default ScreenWorkspacePage;
